import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useMotionValueEvent, useReducedMotion, useScroll } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { GlowFrame } from "./GlowFrame";
import { useLang } from "../i18n";

// Scroll-driven "we fix phones AND laptops" story. Pin + scroll: the visuals
// change scene by scene — owner's cracked iPhone 17 pair, a smashed Windows
// laptop, a broken MacBook, then the fixed-and-shiny ending.
const SCENES = [
  { kind: "iphones" },
  { kind: "photo", src: "/media/broken-laptop-hp.jpg", tone: "clay" },
  { kind: "photo", src: "/media/broken-macbook-stripes.jpg", tone: "volt" },
  { kind: "photo", src: "/media/macbook-desk.jpg", tone: "cyan" },
];

const FloatImg = ({ src, className, delay = 0, style }) => (
  <motion.img
    src={src}
    alt=""
    draggable="false"
    className={className}
    style={style}
    animate={{ y: [0, -12, 0], rotate: [-1.6, 1.6, -1.6] }}
    transition={{ duration: 6.5, delay, repeat: Infinity, ease: "easeInOut" }}
  />
);

const Scene = ({ scene, caption }) => (
  <motion.div
    key={caption}
    className="flex h-full flex-col items-center justify-center gap-8"
    initial={{ opacity: 0, scale: 0.94, y: 24 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.97, y: -18 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
  >
    {scene.kind === "iphones" ? (
      <div className="flex items-end justify-center gap-6 sm:gap-12">
        <FloatImg
          src="/phones/ip17-cracked-screen.png"
          className="h-[34vh] sm:h-[44vh] w-auto drop-shadow-[0_24px_50px_rgba(0,0,0,0.4)]"
        />
        <FloatImg
          src="/phones/ip17-cracked-back.png"
          className="h-[30vh] sm:h-[40vh] w-auto drop-shadow-[0_24px_50px_rgba(46,107,230,0.25)]"
          delay={0.8}
        />
      </div>
    ) : (
      <GlowFrame tone={scene.tone} className="w-[78vw] max-w-md sm:max-w-lg">
        <div className="relative overflow-hidden rounded-[18px] shadow-[0_30px_70px_-20px_rgba(0,0,0,0.5)]">
          <motion.img
            src={scene.src}
            alt={caption}
            className="aspect-[4/3] w-full object-cover"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </GlowFrame>
    )}
    <p data-testid="fixboth-caption" className="max-w-md text-center font-display text-xl sm:text-2xl font-medium text-ink">
      {caption}
    </p>
  </motion.div>
);

export const FixBothScroll = () => {
  const { t } = useLang();
  const fb = t("fixboth");
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => setIdx(Math.min(SCENES.length - 1, Math.floor(v * SCENES.length))));

  const header = (
    <div className="text-center">
      <p className="eyebrow">{fb.eyebrow}</p>
      <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-ink">
        {fb.title}
      </h2>
    </div>
  );

  // Reduced motion / simple path: stacked scenes, no pinning.
  if (reduce) {
    return (
      <section data-testid="fixboth-section" className="border-y border-sand/60 bg-paper px-4 py-16 sm:px-8">
        <div className="mx-auto max-w-6xl space-y-10">
          {header}
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            {SCENES.map((s, i) => (
              <div key={i} className="flex flex-col items-center gap-4">
                {s.kind === "iphones" ? (
                  <div className="flex items-end gap-4">
                    <img src="/phones/ip17-cracked-screen.png" alt="" className="h-48 w-auto" />
                    <img src="/phones/ip17-cracked-back.png" alt="" className="h-44 w-auto" />
                  </div>
                ) : (
                  <img src={s.src} alt={fb.steps[i]} className="w-full rounded-[18px] object-cover" loading="lazy" />
                )}
                <p className="text-center text-sm text-ink/70">{fb.steps[i]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} data-testid="fixboth-section" className="relative h-[340vh] border-y border-sand/60 bg-paper">
      <div className="sticky top-0 flex h-screen flex-col items-center justify-center overflow-hidden px-4">
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-volt/10 blur-3xl"
          aria-hidden="true"
        />
        <div className="absolute inset-x-4 top-[9vh] z-10">{header}</div>
        <div className="relative w-full max-w-4xl flex-1">
          <AnimatePresence mode="wait">
            <Scene key={idx} scene={SCENES[idx]} caption={fb.steps[idx]} />
          </AnimatePresence>
        </div>
        {/* progress dots */}
        <div className="flex items-center gap-2 pb-6" aria-hidden="true">
          {SCENES.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-accent" : "w-1.5 bg-ink/25"}`}
            />
          ))}
        </div>
        {idx === SCENES.length - 1 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="absolute bottom-16">
            <Link
              to="/quote"
              data-testid="fixboth-quote-cta"
              className="group inline-flex items-center gap-2.5 rounded-full bg-accent px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-[#EDF1F7] transition-colors duration-300 hover:bg-volt hover:text-[#0B111B]"
            >
              {fb.cta}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100" />
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
};
